<template>
	<div class="custom-data-table-container">
		<!-- Top Toolbar -->
		<div class="table-toolbar">
			<div class="toolbar-left">
				<button class="ok-btn" @click="saveChanges" title="Save Changes">
					<span class="ok-icon">✓</span> Save Changes
				</button>
				<button @click="handleClearTableCommand('clear1')" class="clear-btn">
					<i class="el-icon-refresh-left mr-1"></i> &nbsp; Clear Table
				</button>
				<!-- <el-dropdown trigger="click" @command="handleClearTableCommand">
					<button class="clear-btn">
						<i class="el-icon-refresh-left mr-1"></i>
						Clear Table <i class="el-icon-arrow-down el-icon--right"></i>
					</button>
					<el-dropdown-menu slot="dropdown">
						<el-dropdown-item command="clear1">Reset to 1 Row</el-dropdown-item>
						<el-dropdown-item command="clear5"
							>Reset to 5 Rows</el-dropdown-item
						>
						<el-dropdown-item command="clearCustom" divided
							>Custom Row Count...</el-dropdown-item
						>
					</el-dropdown-menu>
				</el-dropdown> -->

				<span v-if="saveToast" class="save-toast">
					<b-icon icon="check-circle-fill" class="mr-1"></b-icon> Saved!
				</span>
			</div>

			<div class="toolbar-right">
				<div class="ag-row-action-group">
					<button
						class="ag-action-btn"
						:disabled="selectedRowIndex === null"
						@click="addRowAbove"
					>
						<span class="action-icon">↑</span> Above
					</button>
					<span class="action-btn-divider"></span>
					<button
						class="ag-action-btn"
						:disabled="selectedRowIndex === null"
						@click="deleteSelectedRow"
					>
						<i class="el-icon-delete action-icon"></i> Delete
					</button>
					<span class="action-btn-divider"></span>
					<button
						class="ag-action-btn"
						:disabled="selectedRowIndex === null"
						@click="addRowBelow"
					>
						<span class="action-icon">↓</span> Below
					</button>
				</div>
			</div>
		</div>

		<!-- Table Wrapper -->
		<div class="table-responsive-wrapper" :style="tableStyle">
			<table class="custom-table" v-if="localHeaders.length > 0">
				<thead>
					<tr>
						<th
							v-for="(col, colIdx) in localHeaders"
							:key="colIdx"
							class="table-header-cell"
						>
							<div class="header-content">
								<span class="header-text" :title="getHeaderName(col)">{{
									getHeaderName(col)
								}}</span>
								<div class="header-actions">
									<el-dropdown
										trigger="click"
										@command="handleColumnCommand($event, colIdx)"
									>
										<span
											class="el-dropdown-link header-menu-icon"
											title="Column Options"
										>
											<i class="el-icon-more"></i>
										</span>
										<el-dropdown-menu slot="dropdown">
											<el-dropdown-item command="addLeft"
												>Add Column Left</el-dropdown-item
											>
											<el-dropdown-item command="addRight"
												>Add Column Right</el-dropdown-item
											>
											<!-- <el-dropdown-item command="rename"
												>Rename Column</el-dropdown-item
											> -->
											<el-dropdown-item command="delete" divided
												>Delete Column</el-dropdown-item
											>
										</el-dropdown-menu>
									</el-dropdown>
									<span
										class="header-divider"
										v-if="colIdx < localHeaders.length - 1"
									></span>
								</div>
							</div>
						</th>
					</tr>
				</thead>

				<tbody>
					<tr
						v-for="(row, rowIdx) in localRows"
						:key="rowIdx"
						:class="{ 'selected-row': selectedRowIndex === rowIdx }"
						@click="selectRow(rowIdx)"
					>
						<td
							v-for="(col, colIdx) in localHeaders"
							:key="colIdx"
							class="table-data-cell"
						>
							<textarea
								class="cell-textarea"
								v-model="row[getFieldKey(col)]"
								rows="1"
								@focus="selectRow(rowIdx)"
								@input="autoResizeTextarea($event)"
							></textarea>
						</td>
					</tr>
				</tbody>
			</table>

			<div v-else class="empty-table-msg">
				<span>No Preview / Columns Found</span>
			</div>
		</div>
	</div>
</template>

<script>
import { BIcon } from "bootstrap-vue";
import ElDropdown from "element-ui/lib/dropdown";
import ElDropdownMenu from "element-ui/lib/dropdown-menu";
import ElDropdownItem from "element-ui/lib/dropdown-item";

export default {
	name: "CustomDataTable",
	components: {
		BIcon,
		ElDropdown,
		ElDropdownMenu,
		ElDropdownItem
	},
	props: {
		columnDefs: {
			type: Array,
			default: () => []
		},
		rowData: {
			type: Array,
			default: () => []
		},
		singleRow: {
			type: Object,
			default: () => ({})
		},
		tableStyle: {
			type: [String, Object],
			default: ""
		},
		caller: {
			type: String,
			default: ""
		}
	},
	data() {
		return {
			localHeaders: [],
			localRows: [],
			selectedRowIndex: null,
			saveToast: false
		};
	},
	created() {
		this.initLocalData();
	},
	mounted() {
		this.emitPseudoApi();
	},
	beforeDestroy() {
		this.saveChanges();
	},
	watch: {
		columnDefs: {
			handler(newVal) {
				if (newVal) {
					this.localHeaders = newVal.map(col => ({ ...col }));
				}
			},
			deep: true
		},
		rowData: {
			handler(newVal) {
				if (newVal) {
					this.localRows = newVal.map(row => ({ ...row }));
				}
			},
			deep: true
		}
	},
	methods: {
		initLocalData() {
			this.localHeaders = (this.columnDefs || []).map(col => ({ ...col }));
			this.localRows = (this.rowData || []).map(row => ({ ...row }));
			if (this.localRows.length === 0) {
				this.localRows.push(this.createEmptyRow());
			}
		},
		getHeaderName(col) {
			if (!col) return "";
			return col.headerName || col.label || col.field || "";
		},
		getFieldKey(col) {
			if (!col) return "";
			return col.field || col.label || col.headerName || "";
		},
		createEmptyRow() {
			const newRow = {};
			this.localHeaders.forEach(col => {
				const key = this.getFieldKey(col);
				if (key) {
					newRow[key] = "";
				}
			});
			return newRow;
		},
		selectRow(index) {
			this.selectedRowIndex = index;
		},
		addRowAbove() {
			if (this.selectedRowIndex === null) return;
			const newRow = this.createEmptyRow();
			this.localRows.splice(this.selectedRowIndex, 0, newRow);
			this.emitPseudoApi();
		},
		addRowBelow() {
			const targetIndex =
				this.selectedRowIndex === null
					? this.localRows.length
					: this.selectedRowIndex + 1;
			const newRow = this.createEmptyRow();
			this.localRows.splice(targetIndex, 0, newRow);
			this.selectedRowIndex = targetIndex;
			this.emitPseudoApi();
		},
		deleteSelectedRow() {
			if (this.selectedRowIndex === null) return;
			this.localRows.splice(this.selectedRowIndex, 1);
			if (this.localRows.length === 0) {
				this.localRows.push(this.createEmptyRow());
				this.selectedRowIndex = 0;
			} else if (this.selectedRowIndex >= this.localRows.length) {
				this.selectedRowIndex = this.localRows.length - 1;
			}
			this.emitPseudoApi();
		},
		handleClearTableCommand(command) {
			if (command === "clear1") {
				this.clearTableToCount(1);
			} else if (command === "clear5") {
				this.clearTableToCount(5);
			} else if (command === "clearCustom") {
				this.$prompt("Enter number of empty rows:", "Clear & Reset Table", {
					inputValue: 1,
					confirmButtonText: "Clear Table",
					cancelButtonText: "Cancel"
				})
					.then(({ value }) => {
						const count = parseInt(value, 10);
						if (!isNaN(count) && count > 0) {
							this.clearTableToCount(count);
						}
					})
					.catch(() => {});
			}
		},
		clearTableToCount(rowCount) {
			this.$confirm(
				`Are you sure you want to clear all data and reset the table to ${rowCount} empty row(s)?`,
				"Clear Table",
				{
					confirmButtonText: "Clear All Data",
					cancelButtonText: "Cancel",
					type: "warning"
				}
			)
				.then(() => {
					const newRows = [];
					for (let i = 0; i < rowCount; i++) {
						newRows.push(this.createEmptyRow());
					}
					this.localRows = newRows;
					this.selectedRowIndex = 0;
					this.saveChanges();
				})
				.catch(() => {});
		},
		handleColumnCommand(command, colIdx) {
			if (command === "addLeft" || command === "addRight") {
				this.$prompt("Please enter column header name:", "Add Column", {
					confirmButtonText: "OK",
					cancelButtonText: "Cancel"
				})
					.then(({ value }) => {
						if (!value || !value.trim()) return;
						const headerName = value.trim();
						const fieldKey = headerName;
						const newCol = {
							label: headerName,
							headerName: headerName,
							field: fieldKey,
							cellRenderer: "tableEditor"
						};
						const insertIdx = command === "addLeft" ? colIdx : colIdx + 1;
						this.localHeaders.splice(insertIdx, 0, newCol);
						this.localRows.forEach(row => {
							this.$set(row, fieldKey, "");
						});
						this.emitPseudoApi();
					})
					.catch(() => {});
			} else if (command === "rename") {
				const currentCol = this.localHeaders[colIdx];
				const oldName = this.getHeaderName(currentCol);
				this.$prompt("Please enter new column header name:", "Rename Column", {
					inputValue: oldName,
					confirmButtonText: "OK",
					cancelButtonText: "Cancel"
				})
					.then(({ value }) => {
						if (!value || !value.trim()) return;
						const newName = value.trim();
						this.localHeaders[colIdx].headerName = newName;
						this.localHeaders[colIdx].label = newName;
						this.emitPseudoApi();
					})
					.catch(() => {});
			} else if (command === "delete") {
				const colToDelete = this.localHeaders[colIdx];
				const colName = this.getHeaderName(colToDelete);
				this.$confirm(
					`Are you sure you want to delete column "${colName}"?`,
					"Delete Column",
					{
						confirmButtonText: "Delete",
						cancelButtonText: "Cancel",
						type: "warning"
					}
				)
					.then(() => {
						this.localHeaders.splice(colIdx, 1);
						this.emitPseudoApi();
					})
					.catch(() => {});
			}
		},
		saveChanges() {
			if (this.caller === "StudyCharacteristics") {
				this.$set(
					this.$tera.project.detableStudyCharacteristics,
					"headers",
					this.localHeaders.map(col => ({ ...col }))
				);
				this.$set(
					this.$tera.project.detableStudyCharacteristics,
					"rows",
					this.localRows.map(row => ({ ...row }))
				);
			} else if (this.caller === "StudyOutcomes") {
				this.$set(
					this.$tera.project.detableSudyOutcome,
					"headers",
					this.localHeaders.map(col => ({ ...col }))
				);
				this.$set(
					this.$tera.project.detableSudyOutcome,
					"rows",
					this.localRows.map(row => ({ ...row }))
				);
			}

			this.saveToast = true;
			setTimeout(() => {
				this.saveToast = false;
			}, 2500);

			this.$emit("save", {
				headers: this.localHeaders,
				rows: this.localRows
			});
			this.$emit("updateTable", { e: "Save" });
			this.emitPseudoApi();
		},
		emitPseudoApi() {
			const pseudoApi = {
				getColumnDefs: () => this.localHeaders,
				forEachNode: callback => {
					this.localRows.forEach((row, index) => {
						callback({ data: row, rowIndex: index });
					});
				},
				getSelectedNodes: () => {
					if (
						this.selectedRowIndex !== null &&
						this.localRows[this.selectedRowIndex]
					) {
						return [
							{
								data: this.localRows[this.selectedRowIndex],
								rowIndex: this.selectedRowIndex
							}
						];
					}
					return [];
				},
				getSelectedRows: () => {
					if (
						this.selectedRowIndex !== null &&
						this.localRows[this.selectedRowIndex]
					) {
						return [this.localRows[this.selectedRowIndex]];
					}
					return [];
				}
			};
			this.$emit("api", pseudoApi);
		},
		autoResizeTextarea(event) {
			const el = event.target;
			el.style.height = "auto";
			el.style.height = el.scrollHeight + "px";
		}
	}
};
</script>

<style scoped>
.custom-data-table-container {
	width: 100%;
	background: #ffffff;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	padding: 16px;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.table-toolbar {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px 14px;
	background-color: #fcfcfc;
	border: 1px solid #e4e7ed;
	border-bottom: none;
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	margin-bottom: 0;
}

.toolbar-left {
	display: flex;
	align-items: center;
	gap: 16px;
}

.ok-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 5px 18px;
	border-radius: 20px;
	border: 1px solid #dcdfe6;
	background-color: #ffffff;
	color: #606266;
	font-weight: 600;
	font-size: 0.88rem;
	cursor: pointer;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
	transition: all 0.2s ease;
}

.ok-btn:hover {
	border-color: #c0c4cc;
	background-color: #f5f7fa;
	color: #303133;
}

.ok-icon {
	color: #67c23a;
	font-weight: 700;
}

.clear-btn {
	display: inline-flex;
	align-items: center;
	padding: 5px 14px;
	border-radius: 20px;
	border: 1px solid #dcdfe6;
	background-color: #ffffff;
	color: #606266;
	font-size: 0.88rem;
	cursor: pointer;
	transition: all 0.2s ease;
}

.clear-btn:hover {
	border-color: #e6a23c;
	color: #e6a23c;
	background-color: #fdf6ec;
}

.ag-row-action-group {
	display: inline-flex;
	align-items: center;
	border: 1px solid #dcdfe6;
	border-radius: 20px;
	background-color: #ffffff;
	padding: 2px 4px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.ag-action-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 5px 12px;
	border: none;
	background: transparent;
	color: #606266;
	font-size: 0.88rem;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s ease;
}

.ag-action-btn:hover:not(:disabled) {
	color: #303133;
	background-color: #f4f4f5;
	border-radius: 4px;
}

.ag-action-btn:disabled {
	color: #c0c4cc;
	cursor: not-allowed;
}

.action-icon {
	font-size: 0.9rem;
	color: #909399;
}

.ag-action-btn:hover:not(:disabled) .action-icon {
	color: #303133;
}

.action-btn-divider {
	display: inline-block;
	height: 16px;
	width: 1px;
	background-color: #e4e7ed;
	margin: 0 2px;
}

.save-toast {
	color: #38a169;
	font-weight: 600;
	font-size: 0.9rem;
	animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(-4px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.table-responsive-wrapper {
	overflow-x: auto;
	overflow-y: visible;
	border: 1px solid #edf2f7;
	border-radius: 6px;
}

.custom-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.95rem;
}

.table-header-cell {
	background-color: #f8f9fa;
	color: #1a202c;
	font-weight: 700;
	font-size: 0.9rem;
	padding: 10px 12px;
	border-bottom: 1px solid #e2e8f0;
	border-right: 1px solid #e2e8f0;
	min-width: 180px;
	position: sticky;
	top: 0;
	z-index: 2;
	white-space: nowrap;
}

.header-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
	white-space: nowrap;
	gap: 8px;
}

.header-text {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	font-weight: 700;
	color: #1a202c;
}

.header-actions {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
}

.header-menu-icon {
	cursor: pointer;
	color: #a0aec0;
	font-size: 0.85rem;
	padding: 2px 4px;
	border-radius: 3px;
	transition: all 0.2s ease;
}

.header-menu-icon:hover {
	color: #2b6cb0;
	background-color: #edf2f7;
}

.header-divider {
	display: inline-block;
	height: 14px;
	width: 1px;
	background-color: #cbd5e0;
	margin-left: 2px;
}

.row-select-header {
	width: 40px;
	text-align: center;
	background-color: #f7fafc;
	border-bottom: 2px solid #e2e8f0;
	border-right: 1px solid #edf2f7;
	position: sticky;
	top: 0;
	z-index: 2;
}

.row-select-cell {
	text-align: center;
	vertical-align: middle;
	border-bottom: 1px solid #edf2f7;
	border-right: 1px solid #edf2f7;
	background-color: #faf5ff;
}

.table-data-cell {
	padding: 0;
	border-bottom: 1px solid #edf2f7;
	border-right: 1px solid #edf2f7;
	background-color: #ffffff;
}

.cell-textarea {
	width: 100%;
	border: none;
	outline: none;
	resize: none;
	padding: 10px 12px;
	background: transparent;
	font-family: inherit;
	font-size: 0.95rem;
	color: #2d3748;
	box-sizing: border-box;
	min-height: 42px;
	line-height: 1.4;
}

.cell-textarea:focus {
	background-color: #ebf8ff;
	box-shadow: inset 0 0 0 2px #3182ce;
}

.selected-row .table-data-cell {
	background-color: #f0f7ff !important;
}

.empty-table-msg {
	padding: 40px;
	text-align: center;
	color: #718096;
	font-weight: 600;
	font-style: italic;
}
</style>
